import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Bookmark,
  Compass,
  Heart,
  Home,
  LogOut,
  Settings,
  Sparkles,
  UserPlus,
  Search,
} from "lucide-react";
import { motion } from "framer-motion";
import { signOut } from "firebase/auth";
import useAuthAccess from "../hooks/useAuthAccess";
import { auth } from "../firebase";
import brandLogo from "../Assets/karanki-new-logo.jpg";

const Navbar = () => {
  const { user } = useAuthAccess();
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    localStorage.removeItem("karankiAuth");
    localStorage.removeItem("karankiProfilePhone");
  };

  const desktopNavItems = user
    ? [
        ["Home", "/dashboard"],
        ["Matches", "/matches"],
        ["Interests", "/interests"],
        ["Shortlist", "/shortlisted"],
        ["Settings", "/settings"],
      ]
    : [
        ["Home", "/"],
        ["About", "/about-us"],
        ["Contact", "/contact-us"],
      ];

  const mobileTabs = user
    ? [
        { label: "Home", to: "/dashboard", Icon: Home },
        { label: "Matches", to: "/matches", Icon: Compass },
        { label: "Interests", to: "/interests", Icon: Heart },
        { label: "Shortlist", to: "/shortlisted", Icon: Bookmark },
        { label: "Settings", to: "/settings", Icon: Settings },
      ]
    : [
        { label: "Home", to: "/", Icon: Home },
        { label: "About", to: "/about-us", Icon: Sparkles },
        { label: "Join", to: "/register", Icon: UserPlus },
        { label: "Login", to: "/login", Icon: Settings },
      ];

  const isActive = (to) => {
    if (to === "/") return location.pathname === "/";
    if (to === "/dashboard") return location.pathname === "/dashboard";
    return location.pathname === to || location.pathname.startsWith(`${to}/`);
  };

  const showMobileTopBar = true;

  const hideMobileBottomNav =
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname.startsWith("/create-profile") ||
    location.pathname.startsWith("/my-profile") ||
    location.pathname === "/verify-otp" ||
    location.pathname === "/settings/membership";

  return (
    <>
      {/* ============== DESKTOP NAVBAR ============== */}
      <nav
        className={`sticky top-0 z-50 hidden lg:block transition-all duration-300 ${
          scrolled
            ? "bg-white/80 backdrop-blur-xl border-b border-sand-200 shadow-soft"
            : "bg-white/60 backdrop-blur-md border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex h-[78px] max-w-7xl items-center justify-between gap-6 px-6 xl:px-8">
          <Link to={user ? "/dashboard" : "/"} className="group flex shrink-0 items-center">
            <img
              src={brandLogo}
              alt="Karanki Matrimony"
              className="h-20 w-auto object-contain"
            />
          </Link>

          <div className="flex min-w-0 flex-1 justify-center">
            <div className="flex items-center gap-1 rounded-full border border-sand-200 bg-white/80 p-1.5 shadow-soft backdrop-blur">
              {desktopNavItems.map(([label, to]) => {
                const active = isActive(to);
                return (
                  <Link
                    key={label}
                    to={to}
                    className={`relative rounded-full px-5 py-2.5 text-[14px] font-semibold transition-all ${
                      active
                        ? "text-white"
                        : "text-ink-600 hover:text-brand-700"
                    }`}
                  >
                    {active && (
                      <motion.span
                        layoutId="nav-pill"
                        className="absolute inset-0 -z-10 rounded-full bg-brand-gradient shadow-[0_8px_20px_-6px_rgba(201,2,67,0.45)]"
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                    {label}
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-3">
            {user ? (
              <>
                <button type="button" onClick={handleLogout} className="btn-primary h-11">
                  <LogOut size={16} />
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-ghost h-11">
                  Login
                </Link>
                <Link to="/register" className="btn-primary h-11">
                  Register Free
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ============== MOBILE TOP BAR ============== */}
      {showMobileTopBar && (
        <header
          className={`sticky top-0 z-40 lg:hidden transition-all ${
            scrolled
              ? "bg-white/85 backdrop-blur-xl border-b border-sand-200 shadow-soft"
              : "bg-white/70 backdrop-blur-md border-b border-transparent"
          }`}
        >
          <div className="flex h-[64px] items-center justify-between px-4">
            <Link to={user ? "/dashboard" : "/"} className="flex items-center">
              <img
                src={brandLogo}
                alt="Karanki Matrimony"
                className="h-16 w-auto object-contain"
              />
            </Link>

            <div className="flex items-center gap-2">
              {user ? (
                <>
                  <Link to="/matches" className="btn-icon h-10 w-10">
                    <Search size={17} />
                  </Link>
                  <Link to="/interests" className="btn-icon relative h-10 w-10">
                    <Heart size={17} />
                  </Link>
                </>
              ) : (
                <Link
                  to="/login"
                  className="rounded-full bg-brand-gradient px-4 py-2 text-[13px] font-semibold text-white shadow-glow"
                >
                  Login
                </Link>
              )}
            </div>
          </div>
        </header>
      )}

      {/* ============== MOBILE BOTTOM NAV ============== */}
      {!hideMobileBottomNav && (
        <div
          className="fixed inset-x-0 bottom-0 z-50 border-t border-sand-200 bg-white lg:hidden"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className={`grid ${user ? "grid-cols-5" : "grid-cols-4"}`}>
            {mobileTabs.map(({ label, to, Icon, badge }) => {
              const active = isActive(to);
              return (
                <Link
                  key={label}
                  to={to}
                  className="relative flex flex-col items-center justify-center gap-0.5 py-2"
                >
                  <div
                    className={`relative inline-flex h-9 w-9 items-center justify-center rounded-xl transition ${
                      active
                        ? "bg-brand-gradient text-white shadow-[0_4px_12px_rgba(201,2,67,0.3)]"
                        : "text-ink-400"
                    }`}
                  >
                    <Icon size={18} strokeWidth={2.2} />
                    {badge > 0 && (
                      <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[9px] font-bold text-white">
                        {badge > 9 ? "9+" : badge}
                      </span>
                    )}
                  </div>
                  <span
                    className={`text-[10px] font-semibold ${
                      active ? "text-brand-600" : "text-ink-400"
                    }`}
                  >
                    {label}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
